/**
 * The tools the game covers, and who makes them.
 *
 * This is the single source of truth for every product and vendor name in the
 * game. Levels reference a `toolId`; nothing anywhere types "Atlassian" as a
 * string. That matters for more than tidiness — the product and vendor names
 * appear in the attribution notice, which is a legal record, and a typo that
 * split "Atlassian" from "Atlassian " would silently produce two partners in
 * the menu and two entries in that record.
 *
 * ---------------------------------------------------------------------------
 * A NOTE ON THE WORD "PARTNER"
 *
 * ReleaseTEAM's relationship with these vendors is a commercial partnership,
 * and that is what this file models. It is deliberately NOT what the player is
 * told. Calling them "our partners" on a public page, next to a notice saying
 * the game is not affiliated with or endorsed by them, would be saying two
 * contradictory things on the same screen — and the second one is the one that
 * has to be true. Player-facing copy therefore talks about TOOLS.
 *
 * Use `partner.name` to say who makes a product. Do not add UI that describes
 * a vendor as a partner of ReleaseTEAM without checking that vendor's brand
 * terms first. See ATTRIBUTION.md.
 * ---------------------------------------------------------------------------
 */

/*
 * Ids are plain strings, not a literal union.
 *
 * They were a union while the registry was fixed at build time, which gave a
 * compile error for a typo. Admins can now create tools and partners at
 * runtime, and a union cannot describe a value that does not exist until
 * someone types it. The safety moved to the server, which validates every id
 * on the way in, and to the resolvers below, which never throw on an id they
 * do not recognise.
 */
export type PartnerId = string;

export type ToolId = string;

/** The ids that ship in the bundle, for code that legitimately hardcodes one. */
export const BUILT_IN_TOOLS = ['jira', 'gitlab', 'artifactory', 'helix-core', 'clearcase'] as const;

export interface Partner {
  id: PartnerId;
  /** How the company is normally referred to. */
  name: string;
  /**
   * The entity that owns the trademarks, for the attribution notice. Not the
   * same as `name`: people say "Atlassian", the trademark is Atlassian Pty Ltd.
   */
  legalName: string;
  /**
   * Accent for this vendor's cards and wordmark.
   *
   * Evocative of the vendor's brand, but chosen to clear contrast on the dark
   * desk and to stay clearly distinct from the brass the UI uses for its own
   * controls — a partner accent that reads as "selected" is worse than no
   * accent. These are OUR colours for OUR cards; they are not the vendor's
   * brand colour and must never be presented as such.
   */
  accent: string;
}

export interface Tool {
  id: ToolId;
  partnerId: PartnerId;
  /** Full product name, as the vendor writes it. Used in attribution. */
  name: string;
  /** What players call it. Used in menus and on level cards. */
  short: string;
  /** One line on the picker: what kind of tool this is. */
  blurb: string;
  /** How many levels this tool's finished set is meant to contain. */
  targetLevels: number;
}

export const PARTNERS: Partner[] = [
  {
    id: 'atlassian',
    name: 'Atlassian',
    legalName: 'Atlassian Pty Ltd',
    accent: '#6ba5ff',
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    legalName: 'GitLab Inc.',
    accent: '#ff7a45',
  },
  {
    id: 'jfrog',
    name: 'JFrog',
    legalName: 'JFrog Ltd',
    accent: '#5fd466',
  },
  {
    id: 'perforce',
    name: 'Perforce',
    legalName: 'Perforce Software, Inc.',
    accent: '#4fd1e0',
  },
  {
    id: 'hcl',
    name: 'HCL',
    legalName: 'HCL Technologies Ltd',
    accent: '#c4a0ff',
  },
];

export const TOOLS: Tool[] = [
  {
    id: 'jira',
    partnerId: 'atlassian',
    name: 'Jira Software',
    short: 'Jira',
    blurb: 'Boards, workflows, permissions and the settings behind them.',
    targetLevels: 10,
  },
  {
    id: 'gitlab',
    partnerId: 'gitlab',
    name: 'GitLab',
    short: 'GitLab',
    blurb: 'Pipelines, runners, protected branches and merge request rules.',
    targetLevels: 10,
  },
  {
    id: 'artifactory',
    partnerId: 'jfrog',
    name: 'JFrog Artifactory',
    short: 'Artifactory',
    blurb: 'Repositories, retention, promotion and access.',
    targetLevels: 10,
  },
  {
    id: 'helix-core',
    partnerId: 'perforce',
    name: 'Perforce Helix Core',
    short: 'Helix Core',
    blurb: 'Depots, protections, streams and workspace configuration.',
    targetLevels: 10,
  },
  {
    id: 'clearcase',
    partnerId: 'hcl',
    name: 'HCL ClearCase',
    short: 'ClearCase',
    blurb: 'VOBs, views, config specs and triggers.',
    targetLevels: 10,
  },
];

/*
 * The runtime layer.
 *
 * Compiled PARTNERS and TOOLS are the baseline; admins can add to them and
 * patch them without a deploy. Held in module state and rebuilt whenever the
 * content layer loads, so every resolver below sees one merged view.
 */
let runtimePartners: Partner[] = [];
let runtimeTools: Tool[] = [];

let partnersById = new Map(PARTNERS.map((p) => [p.id, p]));
let toolsById = new Map(TOOLS.map((t) => [t.id, t]));

function reindex() {
  partnersById = new Map();
  toolsById = new Map();
  for (const partner of [...PARTNERS, ...runtimePartners]) partnersById.set(partner.id, partner);
  for (const tool of [...TOOLS, ...runtimeTools]) toolsById.set(tool.id, tool);
}

export function setRuntimeRegistry(partners: Partner[], tools: Tool[]): void {
  runtimePartners = partners;
  runtimeTools = tools;
  reindex();
}

/** Everything a player can be offered: compiled plus runtime, in that order. */
export function allPartners(): Partner[] {
  return [...PARTNERS, ...runtimePartners];
}

export function allTools(): Tool[] {
  return [...TOOLS, ...runtimeTools];
}

/** Was this created at runtime rather than compiled in? */
export function isRuntimePartner(id: PartnerId): boolean {
  return runtimePartners.some((partner) => partner.id === id);
}

export function isRuntimeTool(id: ToolId): boolean {
  return runtimeTools.some((tool) => tool.id === id);
}

/**
 * Look up a tool.
 *
 * Never throws. It used to, on the reasoning that a bad id was a build-time
 * mistake — but ids are no longer guaranteed by the type system, and a level
 * can outlive the tool it names if an admin removes one. A placeholder keeps
 * the level readable and the app standing; throwing here would blank the whole
 * screen over one stale reference.
 */
export function toolById(id: ToolId): Tool {
  return (
    toolsById.get(id) ?? {
      id,
      partnerId: 'unknown',
      name: id,
      short: id,
      blurb: 'This tool is no longer configured.',
      targetLevels: 10,
    }
  );
}

export function partnerById(id: PartnerId): Partner {
  return (
    partnersById.get(id) ?? {
      id,
      name: 'Unknown vendor',
      legalName: 'Unknown',
      accent: '#8ba3c2',
    }
  );
}

/** The company that makes a tool. */
export function partnerForTool(id: ToolId): Partner {
  return partnerById(toolById(id).partnerId);
}

/** Every tool a partner makes, in registry order. */
export function toolsForPartner(id: PartnerId): Tool[] {
  return allTools().filter((tool) => tool.partnerId === id);
}
